// eslint-disable-prototype-builtins

import styles from './information-card.module.scss';

import { Shimmer } from '@flexo/atoms';
import { formatToMaxFourDigits, IconWrapper, resolveCommunityForSite, formatDate as formatDateISO } from '@flexo/general';
import { useKpiData, getOrgRole, ORG_ROLES } from '@flexo/hooks';
import { CALENDAR, MODAL, RIGHT_DRAWER } from '@flexo/reducers';
import {
  navigateToCommunity,
  navigateToMember,
  navigateToSite,
} from '@flexo/resolvers';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import {
  checkItIsToday,
  formatDay,
  formatMonth,
  formatWeek,
  formatYear,
} from './helpers';
import {
  InformationCardContentCurlyTrees,
  InformationCardContentDescription,
  InformationCardContentDynamicLabel,
  InformationCardContentDynamicLabelInline,
  InformationCardContentKeyValue,
  InformationCardContentLabel,
  InformationCardContentLabelValue,
  InformationCardContentLabelValueList,
  InformationCardContentLabelValueUnit,
  InformationCardContentMemberLabelValue,
  InformationCardContentMembersSites,
  InformationCardContentRankingLabelValue,
  InformationCardContentSiteTypeLabelValue,
  InformationCardContentTopSite,
  InformationCardContentTotalSitesCard,
  InformationCardContentValueLabel,
} from './information-card-contents';

const ENV = (import.meta as any).env;

export function InformationCard(props: any) {
  const {
    InformationCard__Icon,
    InformationCard__Value,
    InformationCard__Label,
    InformationCard__Label__Disabled,
    InformationCard__Detail,
  } = styles;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const pathSegment = location.pathname.split('/')[1];
  const { id: urlID } = useParams();
  const metadata = useSelector((state: any) => state.metadata);
  const kpi = useSelector((state: any) => state.kpi);
  const calendar = useSelector((state: any) => state.calendar);
  const user = useSelector((state: any) => state.user);
  // FLEXO-6066: the subtype-based community CO2 / self-sufficiency detail drawers
  // are an end-user-only affordance — admin/supervisor card behaviour is unchanged.
  const isEndUser = getOrgRole(user) === ORG_ROLES.END_USER;
  const { selectedDate, selectedTimeSet } = calendar.hiveCalendar;

  const currencyFields = ['saving', 'benefit', 'economic', 'revenue', 'cost'];

  let entity = 'Site';
  let kpiContext = 'Energy';

  if (pathSegment?.includes('economics')) {
    kpiContext = 'Economics';
  }

  if (pathSegment?.includes('community')) {
    entity = 'Community';
  }
  if (pathSegment?.includes('member')) {
    entity = 'Member';
  }

  if (props?.kpiContext === 'ancillary') {
    kpiContext = 'Ancillary';
  }

  if (props?.kpiContext === 'economics') {
    kpiContext = 'Economics';
  }

  const overviewKpiApiData =
    (kpi[`${kpiContext}${entity}`]?.['1day']?.[0]?.kpi as any) || null;
  const LOADING_STATE = kpi?.[`${kpiContext}KpiData${entity}AsyncStatus`];

  let economicsRanking = [];

  try {
    if (entity === 'Community' && kpiContext === 'Economics') {
      const rawRankings =
        kpi?.communityRanking?.economicsRanking?.[0]?.rankings?.[0]?.site || [];
      economicsRanking = ([...rawRankings] as any).sort((a, b) =>
        a?.ranking > b?.ranking ? 1 : -1
      );
    }
  } catch (error) {
    console.warn(error);
  }

  // Use the new hook for KPI data access
  const { getCardValue: getKpiCardValue, formatValue: formatKpiValue } =
    useKpiData(overviewKpiApiData, kpiContext);

  function getSiteRanking() {
    const siteID: any = (economicsRanking?.[0] as any)?.siteId || null;
    const site =
      (metadata?.entities as any)?.site?.find(
        (_site: any) => _site.siteId === siteID
      ) || null;

    if (site) {
      return `${site?.siteName || ''} - ${site?.siteExternalId || ''}` || '';
    }

    return '';
  }

  const getDate = () => {
    let result = '';
    switch (selectedTimeSet) {
      case 'day':
        if (checkItIsToday(selectedDate)) result = t('widgets.calendar.today');
        else result = formatDay(selectedDate);
        break;

      case 'week':
        result = selectedDate[0]
          ? `${formatWeek(selectedDate)}`
          : 'Select Week';
        break;

      case 'month':
        result = formatMonth(selectedDate);
        break;

      case 'year':
        result = formatYear(selectedDate);
        break;

      default:
        if (
          formatDay(selectedDate) === formatDay([new Date(), new Date()] as any)
        )
          result = t('widgets.calendar.yesterday');
        else result = formatDay(selectedDate);
        break;
    }
    return result;
  };

  const onClickLabel = () => {
    switch (props?.env || '') {
      case 'web':
        if (props?.descriptionCallback === 'communityEnergy') {
          navigateToCommunity('community-energy', urlID as string);
        } else if (props?.descriptionCallback === 'siteEnergy') {
          navigateToSite('site-energy', urlID as string);
        } else if (props?.descriptionCallback === 'communityEconomics') {
          navigateToCommunity('community-economics', urlID as string);
        } else if (props?.descriptionCallback === 'MemberBenefits') {
          navigateToMember('member-economics', urlID as string);
        } else if (props?.descriptionCallback === 'memberEnergy') {
          navigateToMember('member-energy', urlID as string);
        } else if (
          props?.descriptionCallback === 'communityTotalEstimatedBenefits'
        ) {
          dispatch(
            RIGHT_DRAWER.SetDrawer({
              ...props,
              type: 'COMMUNITY_TOTAL_ESTIMATED_BENEFITS',
            })
          );
        } else if (props?.descriptionCallback === 'communityTotalCosts') {
          dispatch(
            RIGHT_DRAWER.SetDrawer({
              ...props,
              type: 'COMMUNITY_TOTAL_COSTS',
            })
          );
        } else if (props?.descriptionCallback === 'memberTotalCosts') {
          dispatch(
            RIGHT_DRAWER.SetDrawer({
              ...props,
              type: 'MEMBER_TOTAL_COSTS',
            })
          );
        } else if (props?.descriptionCallback === 'siteTotalCosts') {
          dispatch(
            RIGHT_DRAWER.SetDrawer({
              ...props,
              type: 'SITE_TOTAL_COSTS',
            })
          );
        } else if (
          props?.descriptionCallback === 'memberTotalEstimatedBenefits'
        ) {
          dispatch(
            RIGHT_DRAWER.SetDrawer({
              ...props,
              type: 'MEMBER_TOTAL_ESTIMATED_BENEFITS',
            })
          );
        } else if (
          props?.descriptionCallback === 'SiteTotalEstimatedBenefits'
        ) {
          dispatch(
            RIGHT_DRAWER.SetDrawer({
              ...props,
              type: 'SITE_TOTAL_ESTIMATED_BENEFITS',
            })
          );
        } else if (
          props?.descriptionCallback === 'communityCo2' ||
          (isEndUser &&
            (props?.subtype === 'communityPollutionCard' ||
              props?.subtype === 'pollutionCard')) // FLEXO-6076 #4: site CO2 modal
        ) {
          // FLEXO-6066: CO2 detail drawer (matches by card subtype so it works
          // across all siteType branches without per-card config). End-user only.
          dispatch(
            RIGHT_DRAWER.SetDrawer({
              ...props,
              type: 'COMMUNITY_CO2',
            })
          );
        } else if (
          props?.descriptionCallback === 'communitySelfSufficiency' ||
          (isEndUser && props?.subtype === 'communitySelfSufficiencyCard')
        ) {
          // FLEXO-6066: Self-sufficiency detail drawer.
          dispatch(
            RIGHT_DRAWER.SetDrawer({
              ...props,
              type: 'COMMUNITY_SELF_SUFFICIENCY',
            })
          );
        } else if (props?.descriptionCallback === 'siteEconomics') {
          navigateToSite('site-economics', urlID as string);
        } else {
          dispatch(
            RIGHT_DRAWER.SetDrawer({
              ...props,
            })
          );
        }

        break;
      default:
        if (!props.details.title) return;

        dispatch(
          MODAL.OpenModal({
            type: 'CARD',
            visible: true,
            show: true,
            props: { ...props },
            content: 'CARD',
          })
        );

        break;
    }
  };

  // FLEXO-6066: the CO2 / self-sufficiency cards are configured with
  // `detail: false` but should be clickable to open their detail drawer.
  // Treat those subtypes as clickable alongside the normal `detail !== false`.
  const isDetailClickable =
    props?.detail !== false ||
    (isEndUser &&
      (props?.subtype === 'communityPollutionCard' ||
        props?.subtype === 'pollutionCard' || // FLEXO-6076 #4: site CO2 card
        props?.subtype === 'communitySelfSufficiencyCard'));

  const getCardValue = (): { value: string; unit: string } => {
    if (!props['totalValue']) return { value: '', unit: '' };
    let cardValue = getKpiCardValue(props['totalValue'], { currencyFields });
    if (props['totalValue'] === 'sites_count') {
      const dateFrom = selectedDate?.[0] ? formatDateISO(new Date(selectedDate[0])) : '';
      const allSites = metadata?.entities?.site || metadata?.entities?.sites || [];
      const sites = allSites.filter(
        (site: any) => dateFrom
          ? resolveCommunityForSite(site, dateFrom) === urlID
          : site.communityId === urlID
      );

      cardValue = {
        value: sites?.length || 0,
        unit: '',
      };
    }

    if (
      props['subtype'] === 'kwickTopSiteCard' ||
      props['subtype'] === 'topSiteCard'
    ) {
      const rankingData =
        entity === 'Community'
          ? kpi.communityRanking?.economicsRanking
          : kpi.memberRanking?.economicsRanking;
      const topSite = (rankingData?.[0] as any)?.rankings?.[0]?.site?.[0];
      const KPI = topSite?.kpi?.find(
        (kpiItem: any) => kpiItem.name === 'total_costs'
      );

      cardValue = {
        value: formatToMaxFourDigits(
          KPI?.value?.value || KPI?.value || '-.-',
          kpiContext === 'Economics'
        ),
        unit: KPI?.unit,
      };
    }

    return cardValue;

    // return (
    //   <div
    //     key={`${props.id}-featured-data`}
    //     // className={`${
    //     //   ENV?.VITE_APP_IS_WEB === 'true' &&
    //     //   styles.InformationCard__Web__FeaturedData__Wrapper
    //     // }`}
    //     className={styles.InformationCard__FeaturedData}
    //   >
    //     <span
    //       key={`${props.id}-featured-data-value`}
    //       className={`featuredData`}
    //       style={{ fontWeight: 200 }}
    //     >
    //       {showSkeleton
    //         ? undefined
    //         : !cardValue?.value && cardValue?.value !== 0
    //         ? '-.-'
    //         : formatKpiValue(cardValue?.value, kpiContext)}
    //     </span>
    //     <span
    //       key={`${props.id}-featured-data-unit`}
    //       className="heading3L"
    //       style={{ fontWeight: 200 }}
    //     >
    //       {' ' + (cardValue?.unit || '')}
    //     </span>
    //   </div>
    // );
  };

  const getCardDetail = () => {
    const cardValue = getKpiCardValue(props['descriptionValue'], {
      currencyFields,
    });

    const _cardValue = getKpiCardValue(props['totalValue'], { currencyFields });

    const hasValue =
      _cardValue?.value !== null &&
      _cardValue?.value !== undefined &&
      _cardValue?.value !== '' &&
      _cardValue?.value !== '-.-';

    switch (props.descriptionType) {
      case 'totalSitesCard':
        return <InformationCardContentTotalSitesCard props={props} />;
      case 'membersSitesCard':
        return (
          <InformationCardContentMembersSites
            id={props.id}
            description={props.description}
          />
        );
      case 'web:label+value|label+value':
      case 'label+value|label+value':
        return (
          <InformationCardContentLabelValueList
            description={props.description}
            overviewKpiApiData={overviewKpiApiData}
            kpiContext={kpiContext}
            currencyFields={currencyFields}
            id={props.id}
          />
        );
      case 'dynamic-label':
        return (
          <InformationCardContentDynamicLabel
            additionalFields={props.additionalFields}
            description={props.description}
            descriptionNoData={props.descriptionNoData}
            id={props.id}
            overviewKpiApiData={overviewKpiApiData}
            hasValue={hasValue}
            kpiContext={props?.kpiContext}
          />
        );
      case 'dynamic-label-inline':
        return (
          <InformationCardContentDynamicLabelInline
            additionalFields={props.additionalFields}
            description={props.description}
            descriptionNoData={props.descriptionNoData}
            id={props.id}
            overviewKpiApiData={overviewKpiApiData}
            hasValue={hasValue}
            kpiContext={props?.kpiContext}
          />
        );
      case 'label':
        return (
          <InformationCardContentLabel
            description={props.description}
            id={props.id}
          />
        );
      case 'topSiteCard':
        return (
          <>
            <InformationCardContentTopSite
              description={props.description}
              descriptionNoData={props.descriptionNoData}
              id={props.id}
            />
          </>
        );
      case 'label+value+unit':
        return (
          <InformationCardContentLabelValueUnit
            description={props.description}
            value={props.value}
            overviewKpiApiData={overviewKpiApiData}
            kpiContext={kpiContext}
            currencyFields={currencyFields}
            id={props.id}
          />
        );
      case 'description':
        return (
          <InformationCardContentDescription description={props.description} />
        );
      case 'keyValue':
        return (
          <InformationCardContentKeyValue
            description={props.description}
            overviewKpiApiData={overviewKpiApiData}
            kpiContext={kpiContext}
            currencyFields={currencyFields}
          />
        );
      case 'value+label':
        return (
          <InformationCardContentValueLabel
            cardValue={cardValue}
            description={props.description}
            id={props.id}
          />
        );
      case 'label+value':
        return (
          <InformationCardContentLabelValue
            description={props.description}
            cardValue={cardValue}
            overviewKpiApiData={overviewKpiApiData}
            kpiContext={kpiContext}
            currencyFields={currencyFields}
            id={props.id}
          />
        );
      case '[member]label+value':
        return (
          <InformationCardContentMemberLabelValue
            KPIs={overviewKpiApiData}
            description={props.description}
            cardValue={cardValue}
            metadata={metadata}
            urlID={urlID}
            id={props.id}
          />
        );
      case 'siteType[label+value]':
        return (
          <InformationCardContentSiteTypeLabelValue
            description={props.description}
            cardValue={cardValue}
            overviewKpiApiData={overviewKpiApiData}
            id={props.id}
          />
        );
      case '{{trees}}':
        return (
          <InformationCardContentCurlyTrees
            description={props.description}
            cardValue={cardValue}
            t={t}
          />
        );
      case 'ranking[label+value]':
        return (
          <InformationCardContentRankingLabelValue
            description={props.description}
            getSiteRanking={getSiteRanking}
            t={t}
            id={props.id}
          />
        );
      default:
        return null;
    }
  };

  const { id } = props;

  if (!props?.id) {
    return <></>;
  }

  const showSkeleton =
    (LOADING_STATE === 'PENDING' &&
      (overviewKpiApiData?.length === 0 || !overviewKpiApiData)) ||
    !props?.id;

  return (
    <div
      className={` ${
        props.gradientBorder && styles.InformationCard__GradientBorder
      }`}
      style={{ height: '100%', position: 'relative' }}
      key={`${props.id}-information-card`}
    >
      {LOADING_STATE === 'PENDING' && <Shimmer borderRadius={'16px'} />}

      <div className={`${styles.InformationCard}`}>
        {props.icon && (
          <span
            key={`${props.id}-icon`}
            className={`${InformationCard__Icon} ${
              ENV?.VITE_APP_IS_WEB === 'true' &&
              styles.InformationCard__Web__Icon
            }`}
          >
            {<IconWrapper iconName={props.icon} />}{' '}
            {props?.isOverview === true && (
              <span
                className={`paragraph color-grey-500 ${styles.InformationCard__DatePicker}`}
                onClick={(e) => {
                  e.stopPropagation();
                  // Remove active marker from any previous anchor
                  document.querySelectorAll('[data-hive-calendar-anchor-active]').forEach(el => el.removeAttribute('data-hive-calendar-anchor-active'));
                  // Mark this span as the active anchor
                  (e.currentTarget as HTMLElement).setAttribute('data-hive-calendar-anchor-active', '');
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  dispatch(CALENDAR.setHiveCalendarAnchor({
                    top: rect.bottom + 4,
                    right: document.documentElement.clientWidth - rect.right,
                  }));
                  dispatch(CALENDAR.setShowHiveCalendar(true));
                }}
              >
                {getDate()}
              </span>
            )}
          </span>
        )}
        {getCardValue()?.value && (
          <div
            className={`${InformationCard__Value} ${
              showSkeleton && styles.InformationCard__Value__Loading
            }`}
          >
            <span className="featuredData">
              {showSkeleton ? '' : getCardValue()?.value || ''}
            </span>
            &nbsp;
            <span className="heading3L">
              {showSkeleton ? '' : getCardValue()?.unit || ''}
            </span>
          </div>
        )}

        <div
          key={`${props.id}-label-value-label-value-array-ranking-label`}
          className={` ${
            showSkeleton && styles.InformationCard__Label__Loading
          } ${
            isDetailClickable
              ? InformationCard__Label
              : InformationCard__Label__Disabled
          }
      heading4`}
          onClick={() => (isDetailClickable ? onClickLabel() : '')}
          // FLEXO-6133 #6e: the drawer trigger must be keyboard-operable and
          // announced — it was a plain div (WCAG 2.1.1). The accessible name
          // comes from the title text content.
          role={isDetailClickable ? 'button' : undefined}
          tabIndex={isDetailClickable ? 0 : undefined}
          onKeyDown={(e) => {
            if (isDetailClickable && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onClickLabel();
            }
          }}
        >
          {parse(t(`widgets.${id}.title`))}
          {
            <IconWrapper
              // className={'InformationCard__Chevron'}
              iconName={isDetailClickable ? 'chevron-right' : ''}
            />
          }
        </div>
        {showSkeleton ? (
          <>
            <span className={styles.InformationCard__Detail__Loading}></span>
            <span className={styles.InformationCard__Detail__Loading}></span>
          </>
        ) : (
          <div
            className={`${InformationCard__Detail} paragraph ${
              ENV?.VITE_APP_IS_WEB === 'true' &&
              styles.InformationCard__Web__Detail
            }`}
          >
            {getCardDetail()}
          </div>
        )}
      </div>
    </div>
  );
}
